import { Icon, Table } from "semantic-ui-react";

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
	getCloudRevenueSubject,
	getCloudRevenueUnitPrice,
	splitCloudRevenues,
} from "astrid-firestore/src/api/costs/utils/cloudRevenueCosts";
import { formatCostToTime } from "astrid-helpers/src/formatCostToTime";
import { languageName } from "astrid-helpers/src/languages";
import money from "astrid-helpers/src/money";
import msToTime from "astrid-helpers/src/msToTime";

import usePriceTypes from "../../../../prices/hooks/usePriceTypes";
import { formatPrice } from "../../../../prices/utils/priceFormatters";

import OrderCostsPriceForm from "./OrderCostsPriceForm";

export default function OrderCosts({ costs, order, firstParty, readOnly = false }) {
	const { t } = useTranslation();

	const { productionCosts, cloudRevenues } = splitCloudRevenues(costs);

	return (
		<>
			{(productionCosts.length > 0 || cloudRevenues.length === 0) && (
				<ProductionCosts costs={productionCosts} order={order} firstParty={firstParty} readOnly={readOnly} />
			)}

			{cloudRevenues.length > 0 && <CloudRevenues costs={cloudRevenues} />}

			<Table basic="very">
				<Table.Row>
					<Table.Cell>
						<strong>
							{order.total ? t("totalExclVAT", "Total excl. VAT") : t("totalDuration", "Total duration")}
						</strong>
					</Table.Cell>

					<Table.Cell textAlign="right">
						<strong>
							{order.total
								? money(order.total, order.currency)
								: msToTime(order.totalAmountProduced * 60 * 1000, false)}
						</strong>
					</Table.Cell>
				</Table.Row>
			</Table>
		</>
	);
}

function ProductionCosts({ costs, order, firstParty, readOnly }) {
	const { t } = useTranslation();

	const priceTypeNames = usePriceTypes();

	const [editCost, setEditCost] = useState(null);

	return (
		<Table basic="very">
			<Table.Row>
				<Table.Cell>{t("production", "Production")}</Table.Cell>
				<Table.Cell>{t("isbn", "ISBN")}</Table.Cell>
				<Table.Cell>{t("language", "Language")}</Table.Cell>
				<Table.Cell>{t("description", "Description")}</Table.Cell>
				<Table.Cell>{t("duration", "Duration")}</Table.Cell>
				<Table.Cell>{t("price", "Price")}</Table.Cell>
				<Table.Cell textAlign="right">{t("sum")}</Table.Cell>
			</Table.Row>

			{costs?.map((cost) => {
				return (
					<Table.Row key={cost.id}>
						<Table.Cell>
							<Link to={`/production/${cost.production?.id}`}>{cost.production?.title}</Link>
						</Table.Cell>

						<Table.Cell>{cost.production?.isbn}</Table.Cell>

						<Table.Cell>{languageName(cost.production?.language)}</Table.Cell>

						<Table.Cell>
							{cost.price?.type === "custom" ? cost.price?.name : priceTypeNames[cost.price?.type] || ""}
						</Table.Cell>

						<Table.Cell>{formatCostToTime({ cost })}</Table.Cell>

						<Table.Cell>
							{cost.price?.price ? (
								formatPrice(cost.price)
							) : (
								<div>
									{t("unknown", "Unknown")}

									{!readOnly && !order.sent && cost.price?.unit === "time" && (
										<Icon
											name="pencil"
											link
											style={{ marginLeft: 6 }}
											onClick={() => setEditCost(cost)}
										/>
									)}
								</div>
							)}
						</Table.Cell>

						<Table.Cell textAlign="right">
							{cost.total ? money(cost.total, cost.price?.currency) : t("unknown", "Unknown")}
						</Table.Cell>
					</Table.Row>
				);
			})}

			{editCost && (
				<OrderCostsPriceForm firstParty={firstParty} cost={editCost} onClose={() => setEditCost(null)} />
			)}
		</Table>
	);
}

function CloudRevenues({ costs }) {
	const { t } = useTranslation();

	const priceTypeNames = usePriceTypes();

	return (
		<Table basic="very">
			<Table.Row>
				<Table.Cell>{t("type", "Type")}</Table.Cell>
				<Table.Cell>{t("articleOrPeriod", "Article / period")}</Table.Cell>
				<Table.Cell>{t("works", "Works")}</Table.Cell>
				<Table.Cell>{t("price", "Price")}</Table.Cell>
				<Table.Cell>{t("billedWorks", "Billed works")}</Table.Cell>
				<Table.Cell textAlign="right">{t("sum")}</Table.Cell>
			</Table.Row>

			{costs.map((cost) => (
				<Table.Row key={cost.id}>
					<Table.Cell>{priceTypeNames[cost.price?.type] || ""}</Table.Cell>

					<Table.Cell>
						{getCloudRevenueSubject(cost)}
						{cost.meta.article?.isbn && <div style={{ opacity: 0.6 }}>{cost.meta.article.isbn}</div>}
					</Table.Cell>

					<Table.Cell>{cost.meta.works ?? ""}</Table.Cell>

					<Table.Cell>{formatPrice(getCloudRevenueUnitPrice(cost))}</Table.Cell>

					<Table.Cell>
						{cost.meta.report?.url && (
							<a href={cost.meta.report.url} target="_blank" rel="noreferrer">
								<Icon name="download" />
								{t("downloadCsv", "CSV")}
							</a>
						)}
					</Table.Cell>

					<Table.Cell textAlign="right">{money(cost.total, cost.price?.currency)}</Table.Cell>
				</Table.Row>
			))}
		</Table>
	);
}
